import type { CSSProperties } from 'react'
import type { BillData } from '../interfaces/exportBill'

// A4 at 96 DPI = 794 × 1123px. The template is designed for this width.
const A4_WIDTH = 794

type BillTemplateProps = BillData

const s: Record<string, CSSProperties> = {
  page: {
    fontFamily: "'NotoSansThai', 'Noto Sans Thai', 'Sarabun', sans-serif",
    fontSize: 12,
    color: '#333',
    margin: 10,
    width: A4_WIDTH - 20,
    boxSizing: 'border-box',
    pageBreakAfter: 'always',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLogo: { width: 180 },
  addressBlock: { marginTop: 8, textAlign: 'right' as const },
  hr: { margin: '8px 0' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    border: '1px solid #000',
    padding: 8,
    verticalAlign: 'middle' as const,
  },
  td: {
    border: '1px solid #000',
    padding: 8,
    verticalAlign: 'middle' as const,
  },
  titleCell: { fontWeight: 'bold', textAlign: 'center' as const },
  roomNumber: { color: 'red', fontWeight: 700, fontSize: 20 },
  warning: { color: 'red', fontWeight: 700, fontSize: 11 },
  meterLabel: {
    textAlign: 'right' as const,
    border: '1px solid #000',
    padding: 8,
    verticalAlign: 'middle' as const,
  },
  amount: { textAlign: 'right' as const },
  bigRed: { color: 'red', fontWeight: 700, textAlign: 'right' as const },
  notesCell: { border: '1px solid #000', padding: 8 },
  notes: { color: 'red' },
  noteItem: { marginBottom: 4 },
  signature: { marginTop: 10 },
  footer: { display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 18 },
  instructions: { color: 'red', flex: 1 },
  instructionsH3: { color: 'red', margin: '0 0 6px 0', fontSize: 13 },
  instructionsOl: { color: 'red', paddingLeft: '1.2em', marginTop: 6 },
  instructionsLi: { color: 'red', marginBottom: 6 },
  contact: { color: 'red', textDecoration: 'underline' as const },
  qrContainer: { flexShrink: 0, marginTop: 0 },
  qrImg: { width: 150 },
}

const fmt = (n: number) => Number(n || 0).toLocaleString()

export function BillTemplate({
  periodLabel,
  roomNumber,
  rent,
  maintenanceFee,
  internet,
  furniture,
  fee,
  otherFeeLabel,
  waterPrev,
  waterNow,
  waterUnits,
  waterUnitPrice,
  waterTotal,
  elecPrev,
  elecNow,
  elecUnits,
  elecUnitPrice,
  electricTotal,
  total,
  qrUrl = '',
  logoUrl = '',
}: BillTemplateProps) {
  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          {logoUrl ? (
            <img style={s.headerLogo} src={logoUrl} alt="logo" />
          ) : (
            <div style={{ width: 180, height: 60 }} />
          )}
        </div>
        <div style={s.addressBlock}>
          <p style={{ margin: '2px 0' }}>ใบแจ้งค่าเช่าห้องพัก สุขกมล อพาร์ทเม้นท์</p>
          <p style={{ margin: '2px 0' }}>
            204 ถนนกาญจนาภิเษก แขวงบางแค เขตบางแค ก.ท.ม 10160 โทร.098-661-6168
            Line: @sukkamol
          </p>
        </div>
      </div>

      <hr style={s.hr} />

      <table style={s.table}>
        <thead>
          <tr>
            <th colSpan={6} style={{ ...s.th, ...s.titleCell }}>
              ค่าบริการประจำเดือน : {periodLabel}
            </th>
          </tr>
          <tr>
            <th style={{ ...s.th, width: '35%' }}>
              <div style={s.roomNumber}>หมายเลขห้อง : {roomNumber}</div>
            </th>
            <th colSpan={5} style={{ ...s.th, width: '65%' }}>
              <div style={s.warning}>
                ****** งดรับเงินสดทุกกรณี โปรดชำระค่าเช่าห้องพักด้วยการโอน ******
                โดยการสแกนคิวอาร์โค้ดด้านล่างขวา เพื่อป้องกันการแพร่กระจายของเชื้อไวรัส
                โควิด-19
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          {/* Column headers */}
          <tr>
            <td style={{ ...s.td, width: '35%', textAlign: 'center' }}>
              <b>รายละเอียดค่าบริการ</b>
            </td>
            <td style={{ ...s.meterLabel, width: '14%' }}>
              <b>เลขมิเตอร์ก่อน</b>
            </td>
            <td style={{ ...s.meterLabel, width: '14%' }}>
              <b>เลขมิเตอร์ใหม่</b>
            </td>
            <td style={{ ...s.meterLabel, width: '10%' }}>
              <b>หน่วย</b>
            </td>
            <td style={{ ...s.meterLabel, width: '13%' }}>
              <b>บาท / หน่วย</b>
            </td>
            <td style={{ ...s.meterLabel, width: '14%' }}>
              <b>รวม (บาท)</b>
            </td>
          </tr>

          {/* ค่าเช่าห้องพัก */}
          <tr>
            <td style={s.td}>
              <b>ค่าเช่าห้องพัก</b>
            </td>
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }}>{fmt(rent)}</td>
          </tr>

          {/* ค่าน้ำประปา */}
          <tr>
            <td style={s.td}>
              <b>ค่าน้ำประปา</b>
            </td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(waterPrev)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(waterNow)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(waterUnits)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(waterUnitPrice)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(waterTotal)}</td>
          </tr>

          {/* ค่าไฟฟ้า */}
          <tr>
            <td style={s.td}>
              <b>ค่าไฟฟ้า</b>
            </td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(elecPrev)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(elecNow)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(elecUnits)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(elecUnitPrice)}</td>
            <td style={{ ...s.td, ...s.amount }}>{fmt(electricTotal)}</td>
          </tr>

          {/* ค่าบริการส่วนกลาง */}
          <tr>
            <td style={s.td}>
              <b>ค่าบริการส่วนกลาง</b>
            </td>
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }}>{fmt(maintenanceFee)}</td>
          </tr>

          {/* ค่าบริการ Internet */}
          <tr>
            <td style={s.td}>
              <b>ค่าบริการ Internet</b>
            </td>
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }}>{fmt(internet)}</td>
          </tr>

          {/* ค่าบริการ Furniture */}
          <tr>
            <td style={s.td}>
              <b>ค่าบริการ Furniture</b>
            </td>
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }}>{fmt(furniture)}</td>
          </tr>

          {/* ค่าปรับ */}
          <tr>
            <td style={s.td}>
              <b>{otherFeeLabel}</b>
            </td>
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }} />
            <td style={{ ...s.td, ...s.amount }}>{fmt(fee)}</td>
          </tr>

          {/* รวมเงิน */}
          <tr>
            <td colSpan={5} style={{ ...s.td, textAlign: 'right' }}>
              <div style={s.bigRed}>รวมเงิน</div>
            </td>
            <td style={{ ...s.td, textAlign: 'right' }}>
              <div style={s.bigRed}>{fmt(total)}</div>
            </td>
          </tr>

          {/* หมายเหตุ */}
          <tr>
            <td colSpan={6} style={s.notesCell}>
              <div style={s.notes}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: 13 }}>
                  ***หมายเหตุ และเงื่อนไข***
                </h3>
                <div style={s.noteItem}>
                  1. โปรดชำระเงินผ่านคิวอาร์โค้ดด้านล่าง ได้ตลอด 24 ชั่วโมง
                  (สามารถชำระได้ทุกธนาคาร ฟรี!! ไม่มีค่าธรรมเนียมการชำระ)
                </div>
                <div style={s.noteItem}>
                  2. ท่านต้องเก็บหลักฐานการชำระเงินไว้
                  พร้อมทั้งส่งหลักฐานการชำระเงินดังกล่าวมายืนยันที่ Line: @sukkamol
                </div>
                <div style={s.noteItem}>
                  3. กรุณาทำการชำระเงินไม่เกินวันที่ 5 ของเดือน หากเกินกำหนดระยะเวลา
                  มีอัตราปรับวันละ 50 บาท
                </div>
                <div style={s.noteItem}>
                  4. การแก้ไขรายการที่ผิดพลาดต้องลงนามกำกับการแก้ไข
                  และห้ามใช้น้ำยาลบคำผิดทุกชนิดโดยเด็ดขาด
                </div>
                <div style={s.noteItem}>
                  5. ใบแจ้งค่าเช่าห้องพักนี้ จะเปลี่ยนสถานะเป็นใบเสร็จรับเงินทันที
                  หลังจากชำระเงินเสร็จสิ้นเรียบร้อยแล้ว
                  พร้อมทั้งมีลายมือชื่อผู้ตรวจสอบหลักฐานการชำระ หรือผู้รับเงิน
                  พร้อมทั้งตราประทับจากสำนักงาน และข้อมูลการชำระเงินในระบบเท่านั้น
                </div>
              </div>
              <div style={s.signature}>
                <div>
                  (ลงลายมือชื่อผู้ตรวจสอบหลักฐานการชำระหรือผู้รับเงิน__________________________)
                </div>
                <br />
                <div>(ตราประทับ______________________)</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Footer: instructions + QR */}
      <div style={s.footer}>
        <div style={s.instructions}>
          <h3 style={s.instructionsH3}>
            ***วิธีการชำระเงินค่าเช่าห้องพักด้วยการโอน โดยการสแกนคิวอาร์โค้ด***
          </h3>
          <ol style={s.instructionsOl}>
            <li style={s.instructionsLi}>
              เปิดแอปพลิเคชันธนาคารในโทรศัพท์มือถือของท่าน (สามารถใช้ชำระได้ทุกธนาคาร
              ไม่มีค่าธรรมเนียม)
            </li>
            <li style={s.instructionsLi}>เลือกเมนูสแกน สแกนเพื่อจ่าย หรือจ่ายด้วยQR</li>
            <li style={s.instructionsLi}>
              สแกนคิวอาร์โค้ด ที่อยู่บนใบแจ้งค่าเช่าห้องพักด้านขวาล่าง
              (โปรดใช้คิวอาร์โค้ดของเดือนล่าสุดเท่านั้น)
            </li>
            <li style={s.instructionsLi}>
              กรอกจำนวนเงินตามใบแจ้งค่าเช่าห้องพัก
              (หากมียอดเงินปรากฏอยู่แล้วสามารถทำขั้นตอนถัดไปได้เลย)
            </li>
            <li style={s.instructionsLi}>
              ตรวจสอบชื่อบัญชี (สุขกมล อพาร์ทเม้นท์) และรหัสร้านค้า หรือเลขอ้างอิง
              (014000001731629)
              <div style={{ fontWeight: 700, color: 'red' }}>
                หากถูกต้อง ให้กดยืนยันการชำระ และใส่รหัสผ่านของท่าน เพื่อยืนยันการทำรายการ
              </div>
            </li>
            <li style={s.instructionsLi}>
              ทำการบันทึกภาพหน้าจอ เพื่อเก็บไว้เป็นหลักฐานการชำระ (ตาม "หมายเหตุ
              และเงื่อนไข" ที่ระบุ(ข้อที่2))
            </li>
          </ol>
          <p style={s.contact}>
            (หากไม่สามารถทำรายการได้ โทร. 098-661-6168 หรือ Line: @sukkamol)
          </p>
        </div>

        <div style={s.qrContainer}>
          {qrUrl ? (
            <img style={s.qrImg} src={qrUrl} alt="QR" />
          ) : (
            <div
              style={{
                width: 150,
                height: 150,
                border: '1px dashed #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                color: '#999',
              }}
            >
              QR
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
